import { DayOfWeek } from './tournament.model';
import type { Club, TournamentFormat, TournamentStatus } from './tournament.model';

/**
 * Time range for a schedule entry
 */
export interface ScheduleTimeRange {
  qty: number;
  courtsQuantity: number;
  startTime: string; // Format: "HH:mm"
  endTime: string; // Format: "HH:mm"
}

/**
 * Schedule entry for a specific date
 */
export interface ScheduleEntry {
  date: string; // Format: "YYYY-MM-DD"
  dayOfWeek: DayOfWeek;
  timeRange: ScheduleTimeRange[];
}

/**
 * Minimal tournament reference carried by a schedule. Fields are optional
 * because they only arrive when the caller requests them via field projection.
 */
export interface ScheduleTournamentRef {
  id: string;
  name?: string;
  status?: TournamentStatus;
  format?: TournamentFormat;
  sport?: { uid: string };
}

/**
 * Schedule model returned from API
 * Note: API returns club as nested object, not clubId
 */
export interface Schedule {
  id: string;
  name: string;
  description?: string;
  club: Club; // Nested club object from API
  schedule: ScheduleEntry[];
  createdAt?: string;
  tournaments?: ScheduleTournamentRef[];
  /** LONG weekly grid: its entries are weekdays with no date. */
  isWeeklyGrid?: boolean;
  /** Week block of a LONG grid: first day of the week it covers. */
  weekStartDate?: string | null;
  /** Filled by the backend on grids already in use, whatever the requested fields. */
  weeklyGridConfig?: WeeklyGridConfig | null;
  /** Slot from which a SHORT tournament plays the bracket (club-local `YYYY-MM-DD` / `HH:mm`). */
  knockoutStartDate?: string | null;
  knockoutStartTime?: string | null;
}

/** Settings a LONG tournament adopts when it picks a weekly grid other tournaments already use. */
export interface WeeklyGridConfig {
  startDayOfWeek: DayOfWeek;
  availabilityDeadlineDay: DayOfWeek | null;
  availabilityDeadlineTime: string | null; // 'HH:mm'
  timezone: string;
}

/** Row of a LONG weekly grid: a weekday with its slots, no date. */
export interface WeeklyGridRow {
  dayOfWeek: DayOfWeek;
  timeRange: ScheduleTimeRange[];
}

/** Request body for creating a LONG weekly grid. */
export interface CreateWeeklyGridRequest {
  name: string;
  isWeeklyGrid: true;
  schedule: WeeklyGridRow[];
}

/**
 * Plain schedule blocks, the only ones SHORT and AMERICAN can use. LONG weekly grids and their week
 * blocks belong to the LONG round cycle and must not be offered anywhere else.
 */
export function isRegularScheduleBlock(schedule: {
  isWeeklyGrid?: boolean;
  weekStartDate?: string | null;
}): boolean {
  return !schedule.isWeeklyGrid && !schedule.weekStartDate;
}

/**
 * Request body for creating a schedule
 */
export interface CreateScheduleRequest {
  name: string;
  description?: string;
  schedule: ScheduleEntry[];
  knockoutStartDate?: string;
  knockoutStartTime?: string;
}

/**
 * Helper function to get day of week from a date
 */
export function getDayOfWeek(date: Date): DayOfWeek {
  const days: DayOfWeek[] = [
    DayOfWeek.SUNDAY,
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
  ];
  return days[date.getDay()];
}

/**
 * Helper to get club ID from a schedule (handles both clubId and club.id)
 */
export function getScheduleClubId(schedule: Schedule): string | null {
  // Check for nested club object first (API response format)
  if (schedule.club && typeof schedule.club === 'object' && schedule.club.id) {
    return schedule.club.id;
  }
  return null;
}
